import { useLocation, UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { useMemo, useEffect, useRef, useState, useContext, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setNotification } from '../actions/appNotifications';
import history from './history';

export const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  }, [value]);
  return ref.current;
}

export const useScrollPosition = () => {
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const updatePosition = () => {
      setScrollPosition(window.scrollY);
    };
    window.addEventListener('scroll', updatePosition);
    updatePosition();
    return () => window.removeEventListener('scroll', updatePosition);
  }, []);

  return scrollPosition;
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined
  });

  useEffect(() => {
    let isMounted = true;
  
    const handleResize = () => {
      if (isMounted) {
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight
        });
      }
    };
  
    window.addEventListener("resize", handleResize);
    handleResize();
  
    return () => {
      isMounted = false;
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return windowSize;
};

export const useOnClickOutside = (ref, handler, excludeClass, altExcludeClass) => {
  useEffect(() => {
    const listener = (event) => {
      if (
        !ref.current ||
        ref.current.contains(event.target) ||
        document.getElementsByClassName(excludeClass)?.[0]?.contains(event.target) ||
        document.getElementsByClassName(altExcludeClass)?.[0]?.contains(event.target)
      ) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
};

export const useNotifications = (pending, error, successConfig, errorConfig) => {
  const dispatch = useDispatch();
  const previous = usePrevious({ pending, error });

  useEffect(() => {
    if (previous?.pending && !pending && !error) {
      dispatch(setNotification(successConfig));
    }
    if (previous?.pending && !pending && error) {
      dispatch(setNotification(errorConfig));
    }
  }, [pending, error]);
};

export function useBlocker(blocker, when = true) {
  const { navigator } = useContext(NavigationContext);
  useEffect(() => {
    if (!when) return;
    const unblock = history.block((tx) => {
      const autoUnblockingTx = {
        ...tx,
        retry() {
          unblock();
          tx.retry();
        }
      };
      blocker(autoUnblockingTx);
    });
    // eslint-disable-next-line consistent-return
    return unblock;
  }, [navigator, blocker, when]);
}
export function usePrompt(message, when = true) {
  const blocker = useCallback(
    (tx) => {
      if (window.confirm(message)) tx.retry();
    },
    [message]
  );
  useBlocker(blocker, when);
}

export const useOnScreen = (ref) => {

  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) =>
      setIntersecting(entry.isIntersecting)
    );

    const currentElement = ref?.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
      observer.disconnect();
    };
  }, [ref]);

  return isIntersecting;
};

export const useDebounce = (callback, delay) => {
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const debouncedCallback = (...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };

  return debouncedCallback;
};
